/**
 * A Mini-games library card's poster: the game at rest. The card draws its mini-game the way the composer previews
 * it (the runtime, the template's bundle, the version's locked document and art) as a still (the runtime's resting
 * scene, no motion, see `PreviewDocBuild.still`), so every card shows its own game (a wheel, gifts, a vault, a meter)
 * over its background, instead of a background plate that looks like every other one.
 */
import type { GameLogos } from './ad-subjects';
import { newestLockedVersion, newestVersion } from './ad-versions';
import type { Playable, PlayableVersion } from './playable-api';
import { buildPlayablePreviewDoc, type PlayableRuntime } from './playable-preview';
import { withGameLogos, withLockedMiniGame } from './welcome-bonus';

/** The format a card draws its game in: the responsive one (every game template offers it), which lays out to any
 *  box, so the game fills the card's poster frame instead of sitting letterboxed in it. */
export const POSTER_FORMAT = 'responsive';

/** The size a card's game is laid out at: the ad library shell's poster frame (4:5, `Poster` in the VideoLibrary
 *  styles) at a phone's width; the frame is scaled into the card, never re-laid out. */
export const POSTER_FRAME = { w: 360, h: 450 } as const;

/** The version a card shows: the newest generated version with a background plate, else the newest with one (a first
 *  run's plate shows as soon as it lands); undefined when nothing has been painted. Pure. */
export const posterVersion = (p: Pick<Playable, 'ad_versions'>): PlayableVersion | undefined => {
  const withPlate = p.ad_versions.filter((v) => Boolean(v.assets.background_url));
  return newestLockedVersion(withPlate) ?? newestVersion(withPlate);
};

/** The card's game at rest: the version's document with its words locked (the composer's preview text) and the
 *  brand's logos as they are now (`withGameLogos`), over its art, mounted as a controlled preview drawn as a still.
 *  Null when the version has no game or the runtime has no bundle for it (the card keeps the plate). Pure. */
export const buildPlayablePosterDoc = (
  runtime: PlayableRuntime,
  version: PlayableVersion,
  logos?: GameLogos,
): string | null => {
  const templateId = version.params.template_id;
  if (!templateId) return null;
  return buildPlayablePreviewDoc(
    runtime,
    templateId,
    POSTER_FORMAT,
    withGameLogos(withLockedMiniGame(version.params), logos),
    version.assets,
    { preview: true, still: true },
  );
};

/** The scale that fits the card's game frame into a `boxW` x `boxH` poster (enlarging too, so a wide card is filled);
 *  0 for a box not laid out yet. Pure. */
export const posterScale = (boxW: number, boxH: number): number => {
  if (!(boxW > 0) || !(boxH > 0)) return 0;
  return Math.min(boxW / POSTER_FRAME.w, boxH / POSTER_FRAME.h);
};
